"use client";

import * as React from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border/50 last:border-0">
            {item.label}
          </Link>
        </div>)}
    </div>;
}
export default function StickyHeader() {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  React.useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  const staticLinks = [{
    label: "Učinkovitije poslovanje",
    href: "/#brži-rast-poslovanja"
  }, {
    label: "Pametno upravljanje",
    href: "/#nadzor-u-stvarnom-vremenu"
  }, {
    label: "Produktivnost",
    href: "/#povećanje-produktivnosti"
  }, {
    label: "Funkcionalnosti",
    href: "/FeaturesPage"
  }, {
    label: "Cijena",
    href: "/PricingPage"
  }, {
    label: "Kontaktirajte nas",
    href: "/ContactUsPage"
  }];
  return <section data-section-id="256847" id="glavna-navigacija" className={cn("sticky top-0 z-50 w-full bg-background transition-all duration-300 font-default", isScrolled ? "shadow-md border-b border-border bg-background/80 backdrop-blur-md" : "border-b border-border")}>
      <header className="container mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <WvcLogo className="h-6 md:h-6 w-auto text-foreground" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center px-8 items-center gap-1">
          <div className="flex items-center">
            {staticLinks.map((link, i) => <Link key={i} data-index={i} to={link.href} className="inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none">
                {link.label}
              </Link>)}
          </div>
          <MenuProvider menu_id="88">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-2 md:gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent/50 rounded-lg">
                <Search className="h-5 w-5" />
                <span className="sr-only">Pretraži</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-3 mr-4 mt-2 bg-background border-border shadow-lg rounded-lg" align="end">
              <div className="flex gap-2">
                <ProductsSearch value={searchQuery} onChange={setSearchQuery} className="flex-1 bg-muted border-border rounded-md" placeholder="Pretražite usluge..." />
                <Button asChild size="icon" className="shrink-0 bg-primary text-primary-foreground rounded-lg shadow-md">
                  <Link to={`/HomePage?s=${encodeURIComponent(searchQuery)}`}>
                    <Search className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Button asChild className="hidden md:flex bg-primary text-primary-foreground font-semibold rounded-xl px-6 py-3 shadow-3xl hover:opacity-90 transition-all">
            <Link to="https://app.mobileosoft.com/login">Prijavi se</Link>
          </Button>

          {}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent/50 rounded-lg">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Otvori izbornik</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px] p-6 bg-background border-l border-border">
              <SheetHeader className="text-left mb-4">
                <SheetTitle asChild>
                  <Link to="/" className="inline-block">
                    <WvcLogo className="h-6 w-auto text-foreground" />
                  </Link>
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 border-b border-border/50 pb-4">
                {staticLinks.map((link, i) => <Link key={i} data-index={i} to={link.href} className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors">
                    {link.label}
                  </Link>)}
              </div>
              <MenuProvider menu_id="88">
                <MobileMenuConsumer />
              </MenuProvider>
              <div className="mt-8">
                <Button asChild className="w-full bg-primary text-primary-foreground font-semibold rounded-xl shadow-3xl">
                  <Link to="https://app.mobileosoft.com/login">Prijavi se</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </section>;
}